import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, fromEvent, map, Observable, of, startWith } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class WindowService {
  private widthObservable: BehaviorSubject<number>;
  private isBrowser: boolean;

  get nativeWindow(): Window {
    if (this.isBrowser) {
      return window;
    }
    return this.getWindowServerSideStub();
  }

  constructor(
    @Inject(PLATFORM_ID) platformId: Record<string, unknown>,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.widthObservable = new BehaviorSubject(this.getWindowSize().width);

    if (this.isBrowser) {
      fromEvent(window, 'resize')
        .subscribe(() => {
          this.widthObservable.next(this.getWindowSize().width);
        });
    }
  }

  public scrollTo(top: number): void {
    this.nativeWindow.scrollTo({
      left: 0,
      top,
      behavior: 'smooth',
    });
  }

  public moveTo(top: number): void {
    this.nativeWindow.scrollTo({
      left: 0,
      top,
    });
  }

  public redirectToUrl(url: string): void {
    this.nativeWindow.location.href = url;
  }

  public evaluatePathnameToAddClassDynamically(pathname: string): boolean {
    const pathnames = ['/register/complementary'];

    if (pathnames.includes(pathname)) {
      return true;
    }

    return false;
  }

  public media(query: string): Observable<boolean> {
    if (this.isBrowser) {
      const mediaQuery = window.matchMedia(query);

      return fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
        startWith(mediaQuery),
        map((list: MediaQueryList) => list.matches),
      );
    }

    return of(false);
  }

  private getWindowSize(): { height: number; width: number;} {
    return {
      height: this.nativeWindow.innerHeight,
      width: this.nativeWindow.innerWidth,
    };
  }

  private getWindowServerSideStub(): Window {
    return {
      scrollTo: () => null,
      location: {
        href: '',
      },
      innerWidth: 0,
      innerHeight: 0,
      scrollY: 0,
    } as unknown as Window;
  }
}
